import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { formatCurrency, calculateDownPayment } from '../utils/helpers';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Bed, Bath, Maximize, MapPin, Check, ArrowLeft, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = typeof window !== 'undefined' ? window.location.origin : process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyDetail = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    appointmentDate: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await axios.get(`${API}/properties/${id}`);
        setProperty(response.data);
      } catch (error) {
        console.error('Error fetching property:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/property-inquiry`, {
        property_id: property.id,
        property_title: property.title,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        appointment_date: formData.appointmentDate || null
      });
      
      toast.success('¡Solicitud Enviada!', {
        description: 'Nos pondremos en contacto contigo pronto.'
      });
      
      setFormData({ name: '', email: '', phone: '', message: '', appointmentDate: '' });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Error', {
        description: 'Hubo un problema al enviar tu solicitud. Inténtalo de nuevo.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getPropertyTypeLabel = (type) => {
    const labels = {
      house: 'Casa',
      apartment: 'Apartamento',
      land: 'Terreno',
      hotel: 'Hotel'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600">Cargando propiedad...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Propiedad no encontrada</h2>
          <Button asChild className="bg-golden-600 hover:bg-golden-700 text-white">
            <Link to="/propiedades">Ver Todas las Propiedades</Link>
          </Button>
        </div>
      </div>
    );
  }

  const downPayment = calculateDownPayment(property.price);
  const minReservation = 15000;
  const isSold = property.status === 'sold';

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      {/* Back Button */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button asChild variant="ghost" className="text-gray-600 hover:text-golden-600">
            <Link to="/propiedades">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver a Propiedades
            </Link>
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Property Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Image Gallery */}
            <div>
              <div className="relative aspect-[16/10] rounded-2xl overflow-hidden mb-4">
                <img
                  src={property.images[selectedImage]}
                  alt={property.title}
                  className={`w-full h-full object-cover ${isSold ? 'grayscale' : ''}`}
                />
                <Badge className="absolute top-4 left-4 bg-golden-600 hover:bg-golden-700 text-white border-0 z-10">
                  {getPropertyTypeLabel(property.type)}
                </Badge>
                {isSold && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20" data-testid="sold-stamp-detail">
                    <div className="bg-green-700 text-white font-extrabold tracking-[0.4em] text-4xl md:text-6xl px-16 py-4 border-[6px] border-white shadow-2xl -rotate-[18deg] uppercase">
                      Vendida
                    </div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-4">
                {property.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`relative aspect-video rounded-lg overflow-hidden ${
                      selectedImage === index ? 'ring-4 ring-golden-600' : 'opacity-70 hover:opacity-100'
                    } transition-all`}
                  >
                    <img src={image} alt={`${property.title} ${index + 1}`} className={`w-full h-full object-cover ${isSold ? 'grayscale' : ''}`} />
                  </button>
                ))}
              </div>
            </div>

            {/* Property Info */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                      {property.title}
                    </h1>
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-5 h-5 mr-2" />
                      <span className="text-lg">{property.location}</span>
                    </div>
                  </div>
                </div>

                <div className="border-t border-b border-gray-200 py-6 my-6">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className={`text-4xl font-bold ${isSold ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                      {formatCurrency(property.price)}
                    </span>
                  </div>
                  {isSold ? (
                    <p className="text-red-600 font-bold uppercase tracking-wider">
                      Esta propiedad ya fue vendida
                    </p>
                  ) : (
                    <p className="text-gray-600">
                      Aparta con solo {formatCurrency(minReservation)} o {formatCurrency(downPayment)} (5% de enganche)
                    </p>
                  )}
                </div>

                {property.type !== 'land' && (
                  <div className="grid grid-cols-3 gap-6 mb-8">
                    {property.bedrooms && (
                      <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                        <Bed className="w-8 h-8 text-golden-600 mb-2" />
                        <span className="text-2xl font-bold text-gray-900">{property.bedrooms}</span>
                        <span className="text-sm text-gray-600">Recámaras</span>
                      </div>
                    )}
                    {property.bathrooms && (
                      <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                        <Bath className="w-8 h-8 text-golden-600 mb-2" />
                        <span className="text-2xl font-bold text-gray-900">{property.bathrooms}</span>
                        <span className="text-sm text-gray-600">Baños</span>
                      </div>
                    )}
                    <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                      <Maximize className="w-8 h-8 text-golden-600 mb-2" />
                      <span className="text-2xl font-bold text-gray-900">{property.area}</span>
                      <span className="text-sm text-gray-600">m²</span>
                    </div>
                  </div>
                )}

                {property.type === 'land' && (
                  <div className="flex justify-center mb-8">
                    <div className="flex flex-col items-center p-6 bg-gray-50 rounded-lg">
                      <Maximize className="w-8 h-8 text-golden-600 mb-2" />
                      <span className="text-2xl font-bold text-gray-900">{property.area} m²</span>
                      <span className="text-sm text-gray-600">Área del terreno</span>
                    </div>
                  </div>
                )}

                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Descripción</h2>
                  <p className="text-gray-600 leading-relaxed">{property.description}</p>
                </div>

                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Características</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {property.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <div className="w-6 h-6 bg-golden-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="w-4 h-4 text-golden-600" />
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Contact Form */}
          <div className="lg:col-span-1">
            <div className="sticky top-28">
              <Card className="border-0 shadow-2xl">
                <CardContent className="p-8">
                  {isSold ? (
                    <div className="bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl p-6 mb-6 text-white text-center" data-testid="sold-banner-detail">
                      <h3 className="text-2xl font-bold mb-2 tracking-wider">PROPIEDAD VENDIDA</h3>
                      <p className="text-gray-200">
                        Esta propiedad ya no está disponible. Te invitamos a conocer otras opciones de nuestro portafolio.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-golden-600 to-golden-800 rounded-xl p-6 mb-6 text-white">
                      <h3 className="text-2xl font-bold mb-2">¡Aparta Ahora!</h3>
                      <p className="text-golden-50">
                        Reserva esta propiedad con solo {formatCurrency(minReservation)}
                      </p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="name">Nombre Completo *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={isSold}
                        className="mt-1.5"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Correo Electrónico *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={isSold}
                        className="mt-1.5"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Teléfono *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        disabled={isSold}
                        className="mt-1.5"
                      />
                    </div>

                    <div>
                      <Label htmlFor="appointmentDate">Fecha Preferida para Cita</Label>
                      <Input
                        id="appointmentDate"
                        name="appointmentDate"
                        type="date"
                        value={formData.appointmentDate}
                        onChange={handleChange}
                        disabled={isSold}
                        className="mt-1.5"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message">Mensaje</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        placeholder="Cuéntanos más sobre tus necesidades..."
                        disabled={isSold}
                        className="mt-1.5"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={submitting || isSold}
                      data-testid="submit-inquiry-btn"
                      className="w-full bg-golden-600 hover:bg-golden-700 text-white py-6 text-lg disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {isSold ? 'Propiedad Vendida' : (submitting ? 'Enviando...' : 'Solicitar Información')}
                    </Button>

                    {isSold && (
                      <Button
                        asChild
                        variant="outline"
                        className="w-full border-golden-600 text-golden-700 hover:bg-golden-50"
                        data-testid="see-other-properties-btn"
                      >
                        <Link to="/propiedades">Ver Otras Propiedades</Link>
                      </Button>
                    )}
                  </form>

                  <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
                    <a href="tel:+525649796366" className="flex items-center space-x-3 text-gray-700 hover:text-golden-600 transition-colors">
                      <Phone className="w-5 h-5" />
                      <span>+52 564 979 6366</span>
                    </a>
                    <a href="mailto:contacto.gudorealstate@gmail.com" className="flex items-center space-x-3 text-gray-700 hover:text-golden-600 transition-colors">
                      <Mail className="w-5 h-5" />
                      <span>contacto.gudorealstate@gmail.com</span>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
